import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import JobStatusBadge from "../../components/Jobs/JobStatusBadge";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "../../components/common/Table";
import { api } from "../../services/api";
import {
  Close,
  PlayArrow,
  Pause,
  Refresh,
} from "@mui/icons-material";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const ModalContent = styled(Card)`
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  padding-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border-bottom: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.hover};
    color: ${(props) =>
      getTheme(props.theme?.isDark).colors.text};
  }
`;

const Section = styled.div`
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(200px, 1fr)
  );
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  text-transform: uppercase;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-weight: 500;
`;

const ActionsBar = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  padding-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border-top: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString();
};

const formatDuration = (ms) => {
  if (!ms) return "N/A";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  return `${(ms / 60000).toFixed(2)}min`;
};

const JobDetailsModal = ({ job, onClose, onRefresh }) => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const [executionHistory, setExecutionHistory] = useState(
    []
  );
  const [loadingHistory, setLoadingHistory] =
    useState(false);

  const fetchExecutionHistory = useCallback(async () => {
    if (!job?.name) return;

    try {
      setLoadingHistory(true);
      const response = await api.getJobExecutionHistory(
        job.name
      );
      setExecutionHistory(response.results || []);
    } catch (err) {
      console.error(
        "Error fetching execution history:",
        err
      );
    } finally {
      setLoadingHistory(false);
    }
  }, [job]);

  useEffect(() => {
    if (job && job.name) {
      fetchExecutionHistory();
    }
  }, [job, fetchExecutionHistory]);

  const handleStartJob = async () => {
    if (
      !window.confirm(
        `"${job.name}" job-unu indi işə salmaq istəyirsiniz?`
      )
    ) {
      return;
    }
    try {
      await api.startJob({ name: job.name });
      alert("Job uğurla işə salındı");
      onRefresh();
      onClose();
    } catch (err) {
      console.error("Error starting job:", err);
      alert("Job-u işə salmaq mümkün olmadı");
    }
  };

  const handlePauseJob = async () => {
    if (
      !window.confirm(
        `"${job.name}" job-unu dayandırmaq istəyirsiniz?`
      )
    ) {
      return;
    }
    try {
      await api.pauseJob({ name: job.name });
      alert("Job uğurla dayandırıldı");
      onRefresh();
      onClose();
    } catch (err) {
      console.error("Error pausing job:", err);
      alert("Job-u dayandırmaq mümkün olmadı");
    }
  };

  const handleResumeJob = async () => {
    try {
      await api.resumeJob({ name: job.name });
      alert("Job uğurla davam etdirildi");
      onRefresh();
      onClose();
    } catch (err) {
      console.error("Error resuming job:", err);
      alert("Job-u davam etdirmək mümkün olmadı");
    }
  };

  if (!job) return null;

  return (
    <ModalOverlay onClick={onClose} theme={theme}>
      <ModalContent
        onClick={(e) => e.stopPropagation()}
        theme={theme}
      >
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>{job.name}</ModalTitle>
          <CloseButton onClick={onClose} theme={theme}>
            <Close />
          </CloseButton>
        </ModalHeader>

        <Section>
          <SectionTitle theme={theme}>
            Job Information
          </SectionTitle>
          <InfoGrid theme={theme}>
            <InfoItem>
              <InfoLabel theme={theme}>Status</InfoLabel>
              <InfoValue theme={theme}>
                <JobStatusBadge status={job.status} />
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel theme={theme}>Type</InfoLabel>
              <InfoValue theme={theme}>
                {job.type === "scheduled_notification"
                  ? "Scheduled Notification"
                  : job.type === "reminder"
                  ? "Reminder"
                  : "Other"}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel theme={theme}>
                Cron Pattern
              </InfoLabel>
              <InfoValue theme={theme}>
                {job.repeatInterval || "N/A"}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel theme={theme}>Next Run</InfoLabel>
              <InfoValue theme={theme}>
                {formatDate(job.nextRunAt)}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel theme={theme}>Last Run</InfoLabel>
              <InfoValue theme={theme}>
                {formatDate(job.lastRunAt)}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel theme={theme}>
                Last Finished
              </InfoLabel>
              <InfoValue theme={theme}>
                {formatDate(job.lastFinishedAt)}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel theme={theme}>
                Fail Count
              </InfoLabel>
              <InfoValue
                theme={theme}
                style={{
                  color:
                    job.failCount > 0
                      ? "#ef4444"
                      : undefined,
                }}
              >
                {job.failCount || 0}
              </InfoValue>
            </InfoItem>
            {job.failReason && (
              <InfoItem style={{ gridColumn: "1 / -1" }}>
                <InfoLabel theme={theme}>
                  Fail Reason
                </InfoLabel>
                <InfoValue
                  theme={theme}
                  style={{ color: "#ef4444" }}
                >
                  {job.failReason}
                </InfoValue>
              </InfoItem>
            )}
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle theme={theme}>
            Execution History
          </SectionTitle>
          <Table theme={theme}>
            <TableHead theme={theme}>
              <TableRow theme={theme}>
                <TableHeader theme={theme}>
                  Run At
                </TableHeader>
                <TableHeader theme={theme}>
                  Finished At
                </TableHeader>
                <TableHeader theme={theme}>
                  Status
                </TableHeader>
                <TableHeader theme={theme}>
                  Duration
                </TableHeader>
                <TableHeader theme={theme}>
                  Error
                </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingHistory ? (
                <TableRow theme={theme}>
                  <TableCell colSpan={5} theme={theme}>
                    Yüklənir...
                  </TableCell>
                </TableRow>
              ) : executionHistory.length === 0 ? (
                <TableRow theme={theme}>
                  <TableCell colSpan={5} theme={theme}>
                    Execution history tapılmadı
                  </TableCell>
                </TableRow>
              ) : (
                executionHistory.map((history, index) => (
                  <TableRow key={index} theme={theme}>
                    <TableCell theme={theme}>
                      {formatDate(history.runAt)}
                    </TableCell>
                    <TableCell theme={theme}>
                      {formatDate(history.finishedAt)}
                    </TableCell>
                    <TableCell theme={theme}>
                      <span
                        style={{
                          color:
                            history.status === "success"
                              ? "#10b981"
                              : "#ef4444",
                        }}
                      >
                        {history.status === "success"
                          ? "Success"
                          : "Failed"}
                      </span>
                    </TableCell>
                    <TableCell theme={theme}>
                      {formatDuration(history.duration)}
                    </TableCell>
                    <TableCell
                      theme={theme}
                      style={{
                        color: "#ef4444",
                        maxWidth: "300px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {history.error || "N/A"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Section>

        <ActionsBar theme={theme}>
          {job.status === "paused" ? (
            <Button onClick={handleResumeJob} theme={theme}>
              <PlayArrow /> Davam et
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handlePauseJob}
              theme={theme}
            >
              <Pause /> Dayandır
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleStartJob}
            theme={theme}
          >
            <PlayArrow /> İndi işə sal
          </Button>
          <Button
            variant="outline"
            onClick={fetchExecutionHistory}
            theme={theme}
          >
            <Refresh /> Yenilə
          </Button>
          <div style={{ flex: 1 }} />
          <Button
            variant="outline"
            onClick={onClose}
            theme={theme}
          >
            <Close /> Bağla
          </Button>
        </ActionsBar>
      </ModalContent>
    </ModalOverlay>
  );
};

export default JobDetailsModal;





